import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api/client";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage, auth } from "../../firebase";
import { v4 as uuid } from "uuid";
import "./index.css";
import Loading from "../Loading";
import logo from "../../assets/originelogo.png";
import AssosiationHelper from "../../components/AssosiationHelper";

type Parfume = {
  id: string;
  userId: string;
  name: string;
  img?: string;
  description?: string;
};

export default function EditParfume() {
  const { parfumeId } = useParams<{ parfumeId: string }>();
  const nav = useNavigate();

  const [parfume, setParfume] = useState<Parfume | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const dropRef = useRef<HTMLDivElement | null>(null);

  async function fetchParfume() {
    setLoading(true);
    try {
      const res = await api<{ ok: true; parfume: Parfume }>(
        `/client/parfume/${parfumeId}`,
        { method: "GET" }
      );
      setParfume(res.parfume);
      setName(res.parfume.name);
      setDesc(res.parfume.description || "");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchParfume();
  }, [parfumeId]);

  // drag’n’drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;

    const prevent = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onEnter = (e: DragEvent) => {
      prevent(e);
      el.classList.add("drop--hover");
    };
    const onLeave = (e: DragEvent) => {
      prevent(e);
      el.classList.remove("drop--hover");
    };
    const onDrop = (e: DragEvent) => {
      prevent(e);
      el.classList.remove("drop--hover");
      const f = e.dataTransfer?.files?.[0];
      if (f) setFile(f);
    };

    el.addEventListener("dragenter", onEnter);
    el.addEventListener("dragover", onEnter);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("dragend", onLeave);
    el.addEventListener("drop", onDrop);

    return () => {
      el.removeEventListener("dragenter", onEnter);
      el.removeEventListener("dragover", onEnter);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("dragend", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, []);

  async function uploadImageIfAny(): Promise<string> {
    if (!file) return parfume?.img || "";
    const uid = auth.currentUser?.uid || "anon";
    const path = `users/${uid}/parfumes/${uuid()}-${file.name}`;
    const r = ref(storage, path);
    await uploadBytes(r, file);
    return await getDownloadURL(r);
  }

  async function save() {
    if (!name || busy) return;
    try {
      setBusy(true);
      const imgUrl = await uploadImageIfAny();

      await api(`/client/parfume/${parfumeId}`, {
        method: "PUT",
        body: JSON.stringify({
          id: parfumeId,
          name,
          img: imgUrl,
          description: desc || "",
        }),
      });

      nav("/profile");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loading />;
  if (!parfume) {
    nav("/profile", { replace: true });
    return null;
  }

  return (
    <div className="cabinet">
      <header className="cabinet__top">
        <img src={logo} className="login__logo" />
        <button
          className="pill pill--ghost"
          onClick={() => {
            nav("/profile");
          }}
        >
          Профиль
        </button>
      </header>

      <div className="add">
        <h1 className="add__title">Редактировать парфюм</h1>

        <div
          ref={dropRef}
          className="dropzone"
          onClick={() => document.getElementById("fileInput")?.click()}
        >
          {file ? (
            <img
              src={URL.createObjectURL(file)}
              alt="preview"
              className="dropzone__preview"
            />
          ) : parfume.img ? (
            <img
              src={parfume.img}
              alt="preview"
              className="dropzone__preview"
            />
          ) : (
            <div className="dropzone__placeholder">
              <span className="dropzone__icon">📷</span>
              <span>Перетащите фото или нажмите для выбора</span>
            </div>
          )}
          <input
            id="fileInput"
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <input
          className="field"
          placeholder="Название на флаконе"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <textarea
          className="field-description"
          placeholder="Ваша ассоциация или история"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />

        <button className="pill-edit" onClick={save} disabled={!name || busy}>
          {busy ? "..." : "Сохранить"}
        </button>

        <AssosiationHelper />
      </div>
    </div>
  );
}
